import type { ReportResponse } from "@utils/viewDefinition/types";
import { parseViewsV2Error, VIEWS_V2_FROZEN_MESSAGE } from "@utils/viewDefinition/errors";
import type { Report } from "./types";

// V2's ViewModule vocabulary ("equity", "imagery", ...) is lowercase and doesn't match
// the uppercase legacy tokens ("EQUITY", "EMOTIONAL", ...) that ReportCard's SCREEN_COLORS
// map, IconManager's VIEWS map, and MODULE_DETAIL are all keyed by. Every legacy-shaped
// consumer of Report.viewModuleTypes/viewScreenTypes expects the uppercase tokens, so the
// adapter normalizes here rather than pushing this translation onto each consumer.
const V2_MODULE_TO_LEGACY_TOKEN: Record<string, string> = {
  equity: "EQUITY",
  visibility: "VISIBILITY",
  imagery: "EMOTIONAL",
  advertising: "ADVERTISING",
  reputation: "REPUTATION",
  conversion: "CONVERSION",
  exclusivequestions: "QUESTIONS",
  // Legacy spelling kept for views saved before the rename; listed before
  // ai_visibility so the inverse map resolves AI_VISIBILITY to ai_visibility.
  aivisibility: "AI_VISIBILITY",
  ai_visibility: "AI_VISIBILITY",
};

export function normalizeModuleToken(module?: string): string {
  if (!module) return "";
  return V2_MODULE_TO_LEGACY_TOKEN[module] ?? module.toUpperCase();
}

export function normalizeScreenTypeToken(screenType?: string): string {
  return screenType ? screenType.toUpperCase() : "";
}

export { V2_MODULE_TO_LEGACY_TOKEN };

const LEGACY_TOKEN_TO_V2_MODULE: Record<string, string> = Object.fromEntries(
  Object.entries(V2_MODULE_TO_LEGACY_TOKEN).map(([module, token]) => [token, module]),
);

/**
 * Inverse of normalizeModuleToken: turns a legacy/route-derived token ("EMOTIONAL",
 * "questions", "equity") into the V2 module vocabulary the V2 API is keyed by
 * ("imagery", "exclusivequestions", "equity"). Tokens with no rename just lowercase.
 */
export function normalizeLegacyTokenToV2Module(token?: string): string {
  if (!token) return "";
  return LEGACY_TOKEN_TO_V2_MODULE[token.toUpperCase()] ?? token.toLowerCase();
}

export function adaptReport(r: ReportResponse): Report {
  return {
    id: r.id,
    name: r.name,
    description: r.description ?? null,
    order: r.sortOrder ?? 0,
    type: r.type,
    viewIds: r.views?.map((v) => v.id) ?? [],
    viewScreenTypes: r.views?.map((v) => normalizeScreenTypeToken(v.screenType)) ?? [],
    viewModuleTypes: r.views?.map((v) => normalizeModuleToken(v.module)) ?? [],
    viewCount: r.viewCount,
  };
}

/** Report create / attach-views cap: the server rejects more ids than this. */
export const MAX_CREATE_VIEW_IDS = 2000;

/**
 * The server tolerates duplicate viewIds on report create / attachViews, but resolves them as
 * "last occurrence's position wins", leaving a gap in the resulting sequence. Sending a unique
 * list in first-occurrence order keeps the positions the user picked.
 */
export function dedupeViewIds(viewIds: string[]): string[] {
  return Array.from(new Set(viewIds));
}

/**
 * Resolves the toast message for a failed Views V2 reports write. SERVICE_NOT_CUT_OVER
 * always shows the frozen-platform copy; every other case falls back to the caller's message.
 */
export async function reportViewsV2WriteErrorMessage(
  err: unknown,
  fallbackMessage: string,
): Promise<string> {
  const body = await parseViewsV2Error(err);
  return body?.code === "SERVICE_NOT_CUT_OVER" ? VIEWS_V2_FROZEN_MESSAGE : fallbackMessage;
}
